/**
 * German names of the real world (theatres/, docs/Theatres.md): the engine words nations,
 * theatres, provinces and towns of the historical maps in English; German presents them with
 * their established German names — "Poland" → "Polen", "Soviet Union" → "die Sowjetunion",
 * "Warsaw" → "Warschau", "Pomerania" → "Pommern". Only traditional exonyms that predate the wars;
 * never occupation-era renamings. Towns without an established German name keep their name.
 */
import type { Gender } from './de';

/** A nation: German name, its gender, and whether it takes the article ("die Sowjetunion"). */
export interface RealNation {
  /** Nominative, e.g. "Deutschland", "Deutsches Kaiserreich". */
  bare: string;
  gender: Gender;
  articled: boolean;
  /** Adjective stem for "the adjective + noun" names ("deutsch" in "Deutsches Kaiserreich"). */
  adjective?: string;
  noun?: string;
  /** Short form for chips and lists. */
  short: string;
}

export const REAL_NATIONS: Record<string, RealNation> = {
  Germany: { bare: 'Deutschland', gender: 'n', articled: false, short: 'Deutschland' },
  'German Empire': {
    bare: 'Deutsches Kaiserreich',
    gender: 'n',
    articled: true,
    adjective: 'deutsch',
    noun: 'Kaiserreich',
    short: 'Deutschland',
  },
  Poland: { bare: 'Polen', gender: 'n', articled: false, short: 'Polen' },
  'Soviet Union': { bare: 'Sowjetunion', gender: 'f', articled: true, short: 'Sowjetunion' },
  'Russian Empire': {
    bare: 'Russisches Kaiserreich',
    gender: 'n',
    articled: true,
    adjective: 'russisch',
    noun: 'Kaiserreich',
    short: 'Russland',
  },
  France: { bare: 'Frankreich', gender: 'n', articled: false, short: 'Frankreich' },
  Italy: { bare: 'Italien', gender: 'n', articled: false, short: 'Italien' },
  Romania: { bare: 'Rumänien', gender: 'n', articled: false, short: 'Rumänien' },
  'United States': {
    bare: 'Vereinigte Staaten',
    gender: 'pl',
    articled: true,
    adjective: 'vereinigt',
    noun: 'Staaten',
    short: 'USA',
  },
  'United Kingdom': {
    bare: 'Vereinigtes Königreich',
    gender: 'n',
    articled: true,
    adjective: 'vereinigt',
    noun: 'Königreich',
    short: 'Großbritannien',
  },
};

/** German stems of the English adjectives of real nations ("Polish" → "polnisch"). */
export const REAL_ADJECTIVES: Record<string, { stem: string; land: string }> = {
  German: { stem: 'deutsch', land: 'Deutschland' },
  Polish: { stem: 'polnisch', land: 'Polen' },
  Soviet: { stem: 'sowjetisch', land: 'Sowjetunion' },
  Russian: { stem: 'russisch', land: 'Russland' },
  French: { stem: 'französisch', land: 'Frankreich' },
  Italian: { stem: 'italienisch', land: 'Italien' },
  Romanian: { stem: 'rumänisch', land: 'Rumänien' },
  American: { stem: 'amerikanisch', land: 'USA' },
  British: { stem: 'britisch', land: 'Großbritannien' },
};

/** Theatre titles and where the war is fought. */
export const REAL_THEATRES: Record<string, { title: string; inside: string }> = {
  'Poland, September 1939': { title: 'Polen, September 1939', inside: 'in Polen' },
  'East Prussia, August 1914': { title: 'Ostpreußen, August 1914', inside: 'in Ostpreußen' },
  'Europe, 1939': { title: 'Europa 1939', inside: 'in Europa' },
  'Moscow, October 1941': { title: 'Moskau, Oktober 1941', inside: 'vor Moskau' },
  'Stalingrad, November 1942': { title: 'Stalingrad, November 1942', inside: 'bei Stalingrad' },
  'Normandy, July 1944': { title: 'Normandie, Juli 1944', inside: 'in der Normandie' },
};

/**
 * Provinces and (neutral) countries of the historical maps: German name and gender — null for
 * names used without an article ("in Pommern"), a gender for those with one ("in der Slowakei")
 * — and, for names with a declined adjective, the dative with its article ("dem Oberen Don").
 */
export const REAL_REGIONS: Record<string, [string, Gender | null, string?]> = {
  // Countries
  Germany: ['Deutschland', null],
  Poland: ['Polen', null],
  Lithuania: ['Litauen', null],
  Latvia: ['Lettland', null],
  Estonia: ['Estland', null],
  Finland: ['Finnland', null],
  'Soviet Union': ['Sowjetunion', 'f'],
  Hungary: ['Ungarn', null],
  Romania: ['Rumänien', null],
  Sweden: ['Schweden', null],
  Norway: ['Norwegen', null],
  Denmark: ['Dänemark', null],
  Netherlands: ['Niederlande', 'pl'],
  Belgium: ['Belgien', null],
  Luxembourg: ['Luxemburg', null],
  Switzerland: ['Schweiz', 'f'],
  Spain: ['Spanien', null],
  Portugal: ['Portugal', null],
  'United Kingdom': ['Vereinigtes Königreich', 'n'],
  Ireland: ['Irland', null],
  Yugoslavia: ['Jugoslawien', null],
  Bulgaria: ['Bulgarien', null],
  Greece: ['Griechenland', null],
  Albania: ['Albanien', null],
  Turkey: ['Türkei', 'f'],
  Slovakia: ['Slowakei', 'f'],
  'Channel Islands': ['Kanalinseln', 'pl'],
  // Provinces
  Brandenburg: ['Brandenburg', null],
  Neumark: ['Neumark', 'f'],
  Pomerania: ['Pommern', null],
  'Lower Silesia': ['Niederschlesien', null],
  'Upper Silesia': ['Oberschlesien', null],
  Saxony: ['Sachsen', null],
  Bohemia: ['Böhmen', null],
  Moravia: ['Mähren', null],
  'East Prussia': ['Ostpreußen', null],
  'West Prussia': ['Westpreußen', null],
  Masuria: ['Masuren', null],
  Danzig: ['Danzig', null],
  'Memel Territory': ['Memelland', 'n'],
  Pomerelia: ['Pommerellen', null],
  'Greater Poland': ['Großpolen', null],
  Kuyavia: ['Kujawien', null],
  Mazovia: ['Masowien', null],
  Kurpie: ['Kurpie', null],
  'Sieradz Land': ['Sieradzer Land', 'n'],
  'Polish Silesia': ['Polnisch-Schlesien', null],
  'Lesser Poland': ['Kleinpolen', null],
  'Sandomierz Land': ['Sandomirer Land', 'n'],
  'Lublin Land': ['Lubliner Land', 'n'],
  Podlachia: ['Podlachien', null],
  'Suwałki Region': ['Suwalki-Gebiet', 'n'],
  'Wilno Land': ['Wilnaer Land', 'n'],
  'Nowogródek Land': ['Nowogródeker Land', 'n'],
  Polesie: ['Polesien', null],
  Volhynia: ['Wolhynien', null],
  'Red Ruthenia': ['Rotruthenien', null],
  Podolia: ['Podolien', null],
  Pokuttya: ['Pokutien', null],
  Samogitia: ['Samogitien', null],
  Samland: ['Samland', 'n'],
  Natangen: ['Natangen', null],
  Ermland: ['Ermland', 'n'],
  Oberland: ['Oberland', 'n'],
  Nadrauen: ['Nadrauen', null],
  'Lithuania Minor': ['Kleinlitauen', null],
  'Vistula Delta': ['Weichseldelta', 'n'],
  Kulmerland: ['Kulmerland', 'n'],
  'Płock Land': ['Land Płock', 'n'],
  'Dobrzyń Land': ['Land Dobrzyń', 'n'],
  'Łomża Land': ['Land Łomża', 'n'],
  'Grodno Land': ['Land Grodno', 'n'],
  'Suwałki Land': ['Land Suwałki', 'n'],
  'Kovno Land': ['Land Kowno', 'n'],
  Silesia: ['Schlesien', null],
  Thuringia: ['Thüringen', null],
  Bavaria: ['Bayern', null],
  Franconia: ['Franken', null],
  Württemberg: ['Württemberg', null],
  Baden: ['Baden', null],
  'Saar-Palatinate': ['Saarpfalz', 'f'],
  Rhineland: ['Rheinland', 'n'],
  Westphalia: ['Westfalen', null],
  Hanover: ['Hannover', null],
  'Schleswig-Holstein': ['Schleswig-Holstein', null],
  Mecklenburg: ['Mecklenburg', null],
  Hesse: ['Hessen', null],
  Austria: ['Österreich', null],
  Tyrol: ['Tirol', null],
  'Île-de-France': ['Île-de-France', 'f'],
  Normandy: ['Normandie', 'f'],
  Brittany: ['Bretagne', 'f'],
  Picardy: ['Picardie', 'f'],
  Flanders: ['Flandern', null],
  Champagne: ['Champagne', 'f'],
  Lorraine: ['Lothringen', null],
  Alsace: ['Elsass', 'n'],
  Burgundy: ['Burgund', null],
  'Franche-Comté': ['Franche-Comté', 'f'],
  Touraine: ['Touraine', 'f'],
  Anjou: ['Anjou', null],
  Poitou: ['Poitou', null],
  Aquitaine: ['Aquitanien', null],
  Gascony: ['Gascogne', 'f'],
  Languedoc: ['Languedoc', 'n'],
  Provence: ['Provence', 'f'],
  Dauphiné: ['Dauphiné', 'f'],
  Savoy: ['Savoyen', null],
  Auvergne: ['Auvergne', 'f'],
  Limousin: ['Limousin', 'n'],
  Berry: ['Berry', 'n'],
  Corsica: ['Korsika', null],
  Algeria: ['Algerien', null],
  Byelorussia: ['Weißrussland', null],
  'Right-Bank Ukraine': ['Rechtsufrige Ukraine', 'f', 'der Rechtsufrigen Ukraine'],
  'Left-Bank Ukraine': ['Linksufrige Ukraine', 'f', 'der Linksufrigen Ukraine'],
  Donbas: ['Donbass', 'm'],
  'Black Sea Steppe': ['Schwarzmeersteppe', 'f'],
  Crimea: ['Krim', 'f'],
  Kuban: ['Kuban', 'm'],
  'Central Black Earth': ['Zentrales Schwarzerdegebiet', 'n', 'dem Zentralen Schwarzerdegebiet'],
  'Moscow Region': ['Moskauer Gebiet', 'n'],
  'Smolensk Land': ['Smolensker Land', 'n'],
  'Upper Volga': ['Obere Wolga', 'f', 'der Oberen Wolga'],
  'Leningrad Region': ['Leningrader Gebiet', 'n'],
  'Novgorod Land': ['Nowgoroder Land', 'n'],
  'Pskov Land': ['Pskower Land', 'n'],
  'Bryansk Land': ['Brjansker Land', 'n'],
  'Ryazan Land': ['Rjasaner Land', 'n'],
  Piedmont: ['Piemont', 'n'],
  Lombardy: ['Lombardei', 'f'],
  Venetia: ['Venetien', null],
  'South Tyrol': ['Südtirol', null],
  'Julian March': ['Julisch Venetien', null],
  Liguria: ['Ligurien', null],
  Emilia: ['Emilia', 'f'],
  Tuscany: ['Toskana', 'f'],
  Umbria: ['Umbrien', null],
  Marche: ['Marken', 'pl'],
  Latium: ['Latium', 'n'],
  Abruzzi: ['Abruzzen', 'pl'],
  Campania: ['Kampanien', null],
  Apulia: ['Apulien', null],
  Basilicata: ['Basilikata', 'f'],
  Calabria: ['Kalabrien', null],
  Sicily: ['Sizilien', null],
  Sardinia: ['Sardinien', null],
  Wallachia: ['Walachei', 'f'],
  Oltenia: ['Oltenien', null],
  Moldavia: ['Moldau', 'f'],
  Bessarabia: ['Bessarabien', null],
  Bukovina: ['Bukowina', 'f'],
  Transylvania: ['Siebenbürgen', null],
  Banat: ['Banat', 'n'],
  Crișana: ['Kreischgebiet', 'n'],
  Maramureș: ['Maramuresch', 'f'],
  Dobruja: ['Dobrudscha', 'f'],
  'Northern Ireland': ['Nordirland', null],
  Zealand: ['Seeland', null],
  'Kalinin Region': ['Kalininer Gebiet', 'n'],
  'Tula Region': ['Tulaer Gebiet', 'n'],
  'Kaluga Region': ['Kalugaer Gebiet', 'n'],
  'Ryazan Region': ['Rjasaner Gebiet', 'n'],
  'Orel Region': ['Orjoler Gebiet', 'n'],
  'Yaroslavl Region': ['Jaroslawler Gebiet', 'n'],
  'Vyazma Land': ['Wjasmaer Land', 'n'],
  'Rzhev Land': ['Rschewer Land', 'n'],
  Meshchera: ['Meschtschora', 'f'],
  'Vitebsk Land': ['Witebsker Land', 'n'],
  'Mogilev Land': ['Mogilewer Land', 'n'],
  'Roslavl Land': ['Roslawler Land', 'n'],
  'Velikiye Luki Land': ['Land Welikije Luki', 'n'],
  Severia: ['Sewerien', null],
  'Khopyor Country': ['Chopjor-Land', 'n'],
  'Medveditsa Valley': ['Medwediza-Tal', 'n'],
  'Kamyshin Land': ['Kamyschiner Land', 'n'],
  'Don-Volga Isthmus': ['Don-Wolga-Landenge', 'f'],
  'Trans-Volga Steppe': ['Transwolga-Steppe', 'f'],
  'Akhtuba Valley': ['Achtuba-Tal', 'n'],
  'Upper Don': ['Oberer Don', 'm', 'dem Oberen Don'],
  Stalingrad: ['Stalingrad', null],
  'Don Bend': ['Donbogen', 'm'],
  'Chir Valley': ['Tschir-Tal', 'n'],
  'Lower Don': ['Unterer Don', 'm', 'dem Unteren Don'],
  'Donets Basin': ['Donezbecken', 'n'],
  'Salsk Steppe': ['Salsker Steppe', 'f'],
  'Kalmyk Steppe': ['Kalmückensteppe', 'f'],
  'Upper Chir': ['Oberer Tschir', 'm', 'dem Oberen Tschir'],
  'Sarpa Lakes': ['Sarpa-Seen', 'pl'],
  'Kalitva Valley': ['Kalitwa-Tal', 'n'],
  'Kalmyk Steppe East': ['Östliche Kalmückensteppe', 'f', 'der Östlichen Kalmückensteppe'],
  Cotentin: ['Cotentin', 'n'],
  'Saint-Lô Bocage': ['Bocage um Saint-Lô', 'f'],
  Bessin: ['Bessin', 'n'],
  'Caen Plain': ['Ebene von Caen', 'f'],
  Kent: ['Kent', null],
  'Home Counties': ['Home Counties', 'pl'],
  'East Anglia': ['Ostanglien', null],
  Wessex: ['Wessex', null],
  'West Country': ['Westengland', null],
  Wales: ['Wales', null],
  Midlands: ['Midlands', 'pl'],
  Lancashire: ['Lancashire', null],
  Yorkshire: ['Yorkshire', null],
  Northumbria: ['Northumbrien', null],
  'Scottish Lowlands': ['Schottisches Tiefland', 'n', 'dem Schottischen Tiefland'],
  Highlands: ['Highlands', 'pl'],
  Coutançais: ['Coutançais', 'n'],
  Avranchin: ['Avranchin', 'n'],
  'Norman Bocage': ['Normannische Bocage', 'f', 'der Normannischen Bocage'],
  'Suisse Normande': ['Normannische Schweiz', 'f', 'der Normannischen Schweiz'],
  "Pays d'Auge": ['Pays d’Auge', 'n'],
  'Pays de Caux': ['Pays de Caux', 'n'],
  Roumois: ['Roumois', 'n'],
  Vexin: ['Vexin', 'n'],
  'Pays de Bray': ['Pays de Bray', 'n'],
  Boulonnais: ['Boulonnais', 'n'],
  Beauce: ['Beauce', 'f'],
  Perche: ['Perche', 'n'],
  Maine: ['Maine', 'n'],
  'Upper Brittany': ['Obere Bretagne', 'f', 'der Oberen Bretagne'],
  Évrecin: ['Évrecin', 'n'],
};

/** Established German names of towns (engine names are English or the local official name). */
export const REAL_TOWNS: Record<string, string> = {
  Warsaw: 'Warschau',
  Prague: 'Prag',
  Kraków: 'Krakau',
  Poznań: 'Posen',
  Lwów: 'Lemberg',
  Wilno: 'Wilna',
  Łódź: 'Lodz',
  Toruń: 'Thorn',
  Bydgoszcz: 'Bromberg',
  Częstochowa: 'Tschenstochau',
  Katowice: 'Kattowitz',
  Brześć: 'Brest-Litowsk',
  Kalisz: 'Kalisch',
  Stanisławów: 'Stanislau',
  Łuck: 'Luzk',
  Równe: 'Rowno',
  Brno: 'Brünn',
  Bratislava: 'Pressburg',
  Olomouc: 'Olmütz',
  Ostrava: 'Mährisch Ostrau',
  Žilina: 'Sillein',
  Prešov: 'Preschau',
  Moscow: 'Moskau',
  Kiev: 'Kiew',
  Rome: 'Rom',
  Vienna: 'Wien',
  Munich: 'München',
  Cologne: 'Köln',
  Nuremberg: 'Nürnberg',
  Brussels: 'Brüssel',
  Belgrade: 'Belgrad',
  Bucharest: 'Bukarest',
  Milan: 'Mailand',
  Naples: 'Neapel',
  Venice: 'Venedig',
  Florence: 'Florenz',
  Genoa: 'Genua',
  Strasbourg: 'Straßburg',
  Kharkov: 'Charkow',
  Dnepropetrovsk: 'Dnepropetrowsk',
  'Rostov-on-Don': 'Rostow am Don',
  Voronezh: 'Woronesch',
  Kalach: 'Kalatsch',
  Kotelnikovo: 'Kotelnikowo',
  Rzhev: 'Rschew',
  Vyazma: 'Wjasma',
  Mozhaysk: 'Moschaisk',
  Volokolamsk: 'Wolokolamsk',
  Yaroslavl: 'Jaroslawl',
  Ryazan: 'Rjasan',
  Orel: 'Orjol',
  Bryansk: 'Brjansk',
  Hanover: 'Hannover',
  Kovno: 'Kowno',
  Novogeorgievsk: 'Nowogeorgiewsk',
  Przasnysz: 'Praschnitz',
  Wilkowiszki: 'Wilkowischken',
  Zhitomir: 'Schitomir',
  Vinnitsa: 'Winniza',
  Zaporozhye: 'Saporoschje',
  Nikolaev: 'Nikolajew',
  Sevastopol: 'Sewastopol',
  Voroshilovgrad: 'Woroschilowgrad',
  Kirovograd: 'Kirowograd',
  Chernigov: 'Tschernigow',
  'Velikiye Luki': 'Welikije Luki',
  Vitebsk: 'Witebsk',
  Mogilev: 'Mogilew',
  Poltava: 'Poltawa',
  Pskov: 'Pskow',
  Novgorod: 'Nowgorod',
  Iași: 'Jassy',
  Cluj: 'Klausenburg',
  Timișoara: 'Temeswar',
  Constanța: 'Konstanza',
  Chișinău: 'Kischinau',
  Cernăuți: 'Czernowitz',
  Galați: 'Galatz',
  Brașov: 'Kronstadt',
  Oradea: 'Großwardein',
  Sibiu: 'Hermannstadt',
  'Cetatea Albă': 'Akkerman',
  Dmitrov: 'Dmitrow',
  Serpukhov: 'Serpuchow',
  Maloyaroslavets: 'Malojaroslawez',
  Yukhnov: 'Juchnow',
  Sukhinichi: 'Suchinitschi',
  Kozelsk: 'Koselsk',
  Belev: 'Beljow',
  Mtsensk: 'Mzensk',
  Efremov: 'Jefremow',
  Gzhatsk: 'Gschatsk',
  Sychevka: 'Sytschowka',
  Staritsa: 'Stariza',
  Torzhok: 'Torschok',
  Ostashkov: 'Ostaschkow',
  Kashin: 'Kaschin',
  Pereslavl: 'Pereslawl',
  'Orekhovo-Zuyevo': 'Orechowo-Sujewo',
  Yegoryevsk: 'Jegorjewsk',
  Karachev: 'Karatschew',
  Zhizdra: 'Schisdra',
  Kirov: 'Kirow',
  Yelnya: 'Jelnja',
  Dorogobuzh: 'Dorogobusch',
  Bolkhov: 'Bolchow',
  Uglich: 'Uglitsch',
  Orsha: 'Orscha',
  Roslavl: 'Roslawl',
  Toropets: 'Toropez',
  Demidov: 'Demidow',
  Dukhovshchina: 'Duchowschtschina',
  Pochinok: 'Potschinok',
  Krichev: 'Kritschew',
  Mstislavl: 'Mstislawl',
  Rudnya: 'Rudnja',
  Velizh: 'Welisch',
  Kholm: 'Cholm',
  Pochep: 'Potschep',
  Unecha: 'Unetscha',
  Bykhov: 'Bychow',
  Kamyshin: 'Kamyschin',
  Krasnoarmeysk: 'Krasnoarmeisk',
  Serafimovich: 'Serafimowitsch',
  Kletskaya: 'Kletskaja',
  Frolovo: 'Frolowo',
  Mikhaylovka: 'Michailowka',
  Uryupinsk: 'Urjupinsk',
  Novoannensky: 'Nowoanninski',
  Ilovlya: 'Ilowlja',
  Dubovka: 'Dubowka',
  'Kapustin Yar': 'Kapustin Jar',
  Vladimirovka: 'Wladimirowka',
  'Chorny Yar': 'Tschorny Jar',
  Pallasovka: 'Pallasowka',
  Veshenskaya: 'Weschenskaja',
  Boguchar: 'Bogutschar',
  Raygorod: 'Raigorod',
  Morozovsk: 'Morosowsk',
  Tatsinskaya: 'Tazinskaja',
  Surovikino: 'Surowikino',
  Oblivskaya: 'Oblivskaja',
  'Nizhne-Chirskaya': 'Nischne-Tschirskaja',
  Tsimlyanskaya: 'Zimljanskaja',
  'Belaya Kalitva': 'Belaja Kalitwa',
  Zimovniki: 'Simowniki',
  Novocherkassk: 'Nowotscherkassk',
  Shakhty: 'Schachty',
  Konstantinovsk: 'Konstantinowsk',
  Karpovka: 'Karpowka',
  Bokovskaya: 'Bokowskaja',
  Chernyshevskaya: 'Tschernyschewskaja',
  Abganerovo: 'Abganerowo',
  Sadovoye: 'Sadowoje',
  Tundutovo: 'Tundutowo',
  Rossosh: 'Rossosch',
  Chertkovo: 'Tschertkowo',
  Millerovo: 'Millerowo',
  Meshkovskaya: 'Meschkowskaja',
  Perelazovsky: 'Perelasowski',
  Kantemirovka: 'Kantemirowka',
};
